// eslint-disable-next-line @typescript-eslint/triple-slash-reference -- ambient global.d.ts has no runtime JS counterpart to import
/// <reference path="./global.d.ts" />
"use client";

import { logError } from "@jetblue/dotcom-fe-logger";
import {
  emit,
  whenUserConsentDecisionKnown,
  type RawDecision,
} from "./provider";
import { invalidateConsentDocuments } from "./synchronization";

type ConsentDecision = RawDecision;

type PrivacyManagerApiMessage = {
  PrivacyManagerAPI?: {
    capabilities?: object;
    action?: string;
  };
};

const PRIVACY_MANAGER_API_GLOBAL_KEY = "PrivacyManagerAPI";
const GET_GDPR_CONSENT_DECISION_METHOD = "getGDPRConsentDecision";
const GET_CONSENT_DECISION_MESSAGE_ACTION = "getConsentDecision";
const GDPR_PREF_ALLOWS_EVENT_PREFIX = "GDPR Pref Allows ";

const announcedGdprLabels = new Set<number>();

const dispatchGdprLabels = (labels: ConsentDecision) => {
  for (const label of labels) {
    if (announcedGdprLabels.has(label)) {
      continue;
    }

    globalThis.dataLayer?.push({
      event: GDPR_PREF_ALLOWS_EVENT_PREFIX + String(label),
    });
    announcedGdprLabels.add(label);
  }

  for (const label of announcedGdprLabels) {
    if (!labels.includes(label)) {
      announcedGdprLabels.delete(label);
    }
  }
};

const handleConsentDecision = (decision: ConsentDecision) => {
  dispatchGdprLabels(decision);

  const emission = emit(decision);
  if (emission === "changed") {
    invalidateConsentDocuments();
  }
};

const state: {
  trustedConsentOrigin?: string;
  consentResolutionPromise?: Promise<void>;
  isConsentChangeListenerRegistered: boolean;
} = { isConsentChangeListenerRegistered: false };

export const setTrustedConsentOrigin = (origin: string) => {
  state.trustedConsentOrigin = origin;
};

const isAcceptedConsentMessageOrigin = (origin: string) =>
  origin === globalThis.location.origin ||
  origin === state.trustedConsentOrigin;

const readAssertedConsentDecision = () => {
  const { consentDecision, source } =
    globalThis.PrivacyManagerAPI?.callApi(
      GET_GDPR_CONSENT_DECISION_METHOD,
      globalThis.location.host,
    ) ?? {};

  return source === "asserted" && consentDecision ? consentDecision : undefined;
};

const parseConsentMessage = (data: unknown) => {
  if (typeof data !== "string") {
    return;
  }

  try {
    return JSON.parse(data) as PrivacyManagerApiMessage;
  } catch {
    // The page receives postMessages from unrelated scripts. Anything that is
    // not JSON is simply not ours, so this is expected rather than a failure.
    return;
  }
};

const handleConsentMessage = (event: MessageEvent) => {
  if (!isAcceptedConsentMessageOrigin(event.origin)) {
    return;
  }

  const parsed = parseConsentMessage(event.data);
  if (
    !parsed?.PrivacyManagerAPI?.capabilities ||
    parsed.PrivacyManagerAPI.action !== GET_CONSENT_DECISION_MESSAGE_ACTION
  ) {
    return;
  }

  try {
    const decision = readAssertedConsentDecision();
    if (decision) {
      handleConsentDecision(decision);
    }
  } catch (error) {
    // A subscriber threw while reacting to a consent change, which means a
    // vendor may not have started or stopped. Never silent: this is the only
    // place it surfaces.
    void logError({
      error,
      message: "Failed to publish a TrustArc consent change",
    });
  }
};

export const registerConsentChangeListener = () => {
  if (
    state.isConsentChangeListenerRegistered ||
    typeof globalThis.addEventListener !== "function"
  ) {
    return;
  }

  state.isConsentChangeListenerRegistered = true;
  globalThis.addEventListener("message", handleConsentMessage);
};

const PRIVACY_MANAGER_API_POLL_MAX_CHECKS = 40;
const PRIVACY_MANAGER_API_POLL_MIN_INTERVAL_MS = 100;
const PRIVACY_MANAGER_API_POLL_MAX_INTERVAL_MS = 1000;
const PRIVACY_MANAGER_API_POLL_PLATEAU_CHECKS = 10;
const PRIVACY_MANAGER_API_POLL_GROWTH_FACTOR = 1.3;

const getPrivacyManagerApiPollIntervalMs = (checkNumber: number) => {
  const checksPastPlateau =
    checkNumber - PRIVACY_MANAGER_API_POLL_PLATEAU_CHECKS;
  if (checksPastPlateau <= 0) {
    return PRIVACY_MANAGER_API_POLL_MIN_INTERVAL_MS;
  }

  return Math.min(
    PRIVACY_MANAGER_API_POLL_MAX_INTERVAL_MS,
    PRIVACY_MANAGER_API_POLL_MIN_INTERVAL_MS *
      PRIVACY_MANAGER_API_POLL_GROWTH_FACTOR ** checksPastPlateau,
  );
};

const pollUntilPrivacyManagerApiIsAvailable = (
  maxChecks = PRIVACY_MANAGER_API_POLL_MAX_CHECKS,
): Promise<void> =>
  new Promise((resolve, reject) => {
    if (Object.hasOwn(globalThis, PRIVACY_MANAGER_API_GLOBAL_KEY)) {
      resolve();
      return;
    }

    let checks = 0;
    const scheduleNextCheck = () => {
      setTimeout(() => {
        checks++;
        if (Object.hasOwn(globalThis, PRIVACY_MANAGER_API_GLOBAL_KEY)) {
          resolve();
        } else if (checks >= maxChecks) {
          reject(new Error("PrivacyManagerAPI never became available"));
        } else {
          scheduleNextCheck();
        }
      }, getPrivacyManagerApiPollIntervalMs(checks));
    };

    scheduleNextCheck();
  });

export const resolveConsentDecision = async (): Promise<void> => {
  state.consentResolutionPromise ??= (async () => {
    let existingDecision: ConsentDecision | undefined;
    try {
      await pollUntilPrivacyManagerApiIsAvailable();
      existingDecision = readAssertedConsentDecision();
    } catch (error) {
      // We were unable to determine the consent decision at all, so fail
      // closed until a decision is known.
      handleConsentDecision([]);
      state.consentResolutionPromise = undefined;
      throw error;
    }

    // The decision itself is known at this point. If a subscriber throws
    // while reacting to it, that is not a failure to determine consent, so
    // it should propagate without also failing closed.
    if (existingDecision && existingDecision.length > 0) {
      handleConsentDecision(existingDecision);
    }

    globalThis.postMessage(
      JSON.stringify({
        PrivacyManagerAPI: {
          action: GET_CONSENT_DECISION_MESSAGE_ACTION,
          self: globalThis.location.host,
          timestamp: Date.now(),
        },
      }),
      globalThis.location.origin,
    );

    try {
      await whenUserConsentDecisionKnown();
    } catch (error) {
      handleConsentDecision([]);
      state.consentResolutionPromise = undefined;
      throw error;
    }
  })();

  return state.consentResolutionPromise;
};
